import { lazy, Suspense, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Mail } from 'lucide-react';
import { AuthModal } from '@/components/auth/AuthModal';
import HeroSection from '@/components/landing/HeroSection';
import SocialProofBar from '@/components/landing/SocialProofBar';
import ProblemSolution from '@/components/landing/ProblemSolution';
import HowItWorks from '@/components/landing/HowItWorks';

// Lazy load components below the fold for better initial page load
const FeaturesGrid = lazy(() => import('@/components/landing/FeaturesGrid'));
const LiveDemo = lazy(() => import('@/components/landing/LiveDemo'));
const PricingSection = lazy(() => import('@/components/landing/PricingSection'));
const Testimonials = lazy(() => import('@/components/landing/Testimonials'));
const UseCases = lazy(() => import('@/components/landing/UseCases'));
const FAQ = lazy(() => import('@/components/landing/FAQ'));
const FinalCTA = lazy(() => import('@/components/landing/FinalCTA'));
const Footer = lazy(() => import('@/components/landing/Footer'));

export function LandingPage() {
  const navigate = useNavigate();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');

  const openAuthModal = (mode: 'login' | 'register') => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Authentication Modal */}
      <AuthModal
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        defaultMode={authMode}
        onSuccess={() => navigate('/app')}
      />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="/" className="flex items-center space-x-2">
              <Mail className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">FormTo.cc</span>
            </a>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">
                Pricing
              </a>
              <a href="/docs" className="text-gray-600 hover:text-gray-900 transition-colors">
                Docs
              </a>
              <a href="#faq" className="text-gray-600 hover:text-gray-900 transition-colors">
                FAQ
              </a>
            </div>

            {/* CTA Button */}
            <div className="flex items-center">
              <Button onClick={() => openAuthModal('login')}>Login</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main>
        {/* Hero Section */}
        <HeroSection onOpenAuth={openAuthModal} />

        {/* Social Proof Bar */}
        <SocialProofBar />

        {/* Problem/Solution */}
        <ProblemSolution />

        {/* How It Works */}
        <HowItWorks onOpenAuth={openAuthModal} />

        {/* Lazy-loaded sections below the fold */}
        <Suspense fallback={<div className="min-h-screen" />}>
          {/* Features Grid */}
          <div id="features">
            <FeaturesGrid />
          </div>

          {/* Live Demo */}
          <LiveDemo />

          {/* Pricing */}
          <PricingSection onOpenAuth={openAuthModal} />

          {/* Testimonials */}
          <Testimonials />

          {/* Use Cases */}
          <UseCases />

          {/* FAQ */}
          <FAQ />

          {/* Final CTA */}
          <FinalCTA onOpenAuth={openAuthModal} />
        </Suspense>
      </main>

      {/* Footer */}
      <Suspense fallback={<div className="min-h-20" />}>
        <Footer />
      </Suspense>
    </div>
  );
}
